/**
 * Plans v2 card 3.3: how many of each capped kind the user has ON THIS
 * MACHINE, next to the plan's cap, for PlanLimitsPanel and the create
 * buttons ("3 / 1 boards: upgrade to Basic for unlimited").
 *
 * Data over the cap is never touched: it stays readable, editable, deletable
 * and exportable. These rows only say where the user stands; creating more
 * is refused by plans/localGate.js, which opens the upgrade.
 *
 * UI only (it imports the stores the create points count with). The MCP path
 * counts through its own handlers and must not import this file.
 */
import i18n from '../i18n';
import { useEntitlementsStore } from '../store/entitlementsStore';
import { useVaultStore } from '../store/vaultStore';
import { countOwnLocalApi } from './localApiUsage';
import { extraLocalWorkspaceCount } from './localWorkspaces';
import { LOCAL_KINDS, LOCAL_UPGRADE_PLAN } from './localLimits';

/** Vault item type per vault-backed kind. */
const VAULT_TYPE = { dbConnections: 'dbConnection', hosts: 'sftp' };

const visible = (list) => (Array.isArray(list) ? list : []).filter((it) => !String(it?.name || '').startsWith('__'));

async function vaultCount(type) {
  const loaded = useVaultStore.getState().items;
  if (Array.isArray(loaded) && loaded.length > 0) return visible(loaded).filter((it) => it?.type === type).length;
  const { isTauri } = await import('../utils/isTauri');
  if (!isTauri()) return null;
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return visible(await invoke('vault_list')).filter((it) => it?.type === type).length;
  } catch {
    return null; // locked or unavailable: unknown, the row is left out
  }
}

/** The counters, lazily imported so a plain panel render does not load every extension store. */
const COUNTERS = {
  boards: async () => (await (await import('../extensions/bundled/task-manager/boardsStore')).countOwnBoardsAndTasks()).boards,
  tasks: async () => (await (await import('../extensions/bundled/task-manager/boardsStore')).countOwnBoardsAndTasks()).tasks,
  documents: async () => (await import('../extensions/bundled/documents/documentsStore')).countOwnDocuments(),
  calendarEvents: async () => (await import('../extensions/bundled/calendar/calendarStore')).countOwnEvents(),
  // Across every own local workspace, as the create points count them.
  collections: async () => (await countOwnLocalApi()).collections,
  requests: async () => (await countOwnLocalApi()).requests,
  environments: async () => (await countOwnLocalApi()).environments,
  dbConnections: () => vaultCount(VAULT_TYPE.dbConnections),
  hosts: () => vaultCount(VAULT_TYPE.hosts),
  workspaces: async () => extraLocalWorkspaceCount(),
};

/** The kinds the current plan caps (Free: all of them; paid: none). */
export function cappedKinds() {
  const limits = useEntitlementsStore.getState().limits || {};
  return Object.keys(LOCAL_KINDS).filter((k) => limits[k] !== null && limits[k] !== undefined);
}

/** `used` of `kind`, or null when it cannot be counted right now. */
export async function countLocal(kind) {
  const fn = COUNTERS[kind];
  if (!fn) return null;
  try {
    const n = await fn();
    return Number.isInteger(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * One row: `{ kind, used, limit, full, text }`. `full` = no room for one more
 * (at or over the cap). `text` = "3 / 1 boards: upgrade to Basic for unlimited".
 */
export function capRow(kind, used, limit = useEntitlementsStore.getState().limits?.[kind]) {
  if (limit === null || limit === undefined || used == null) return null;
  return { kind, used, limit, full: used >= limit, text: capRowText(kind, used, limit) };
}

export function capRowText(kind, used, limit) {
  return i18n.t('plans.localCaps.row', {
    used,
    limit,
    what: i18n.t(`plans.localLimit.kind.${kind}`, { count: Math.max(used, limit) }),
    plan: i18n.t(`plans.localCaps.plan.${LOCAL_UPGRADE_PLAN}`),
  });
}

/** Every capped kind's row (kinds that cannot be counted right now are left out). */
export async function localCapRows() {
  const kinds = cappedKinds();
  const counts = await Promise.all(kinds.map((k) => countLocal(k)));
  return kinds.map((k, i) => capRow(k, counts[i])).filter(Boolean);
}
